import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Calculator, Target, Activity, Droplets, Scale, 
  ChevronRight, ChevronLeft, Check, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const SPORTS_LIST = [
  { id: 'football', name: 'Football' },
  { id: 'basketball', name: 'Basketball' },
  { id: 'baseball', name: 'Baseball' },
  { id: 'softball', name: 'Softball' },
  { id: 'soccer', name: 'Soccer' },
  { id: 'volleyball', name: 'Volleyball' },
  { id: 'wrestling', name: 'Wrestling' },
  { id: 'swimming', name: 'Swimming' },
  { id: 'track_sprint', name: 'Track - Sprint' },
  { id: 'track_distance', name: 'Track - Distance' },
  { id: 'cross_country', name: 'Cross Country' },
  { id: 'tennis', name: 'Tennis' },
  { id: 'golf', name: 'Golf' },
  { id: 'lacrosse', name: 'Lacrosse' },
  { id: 'field_hockey', name: 'Field Hockey' },
  { id: 'gymnastics', name: 'Gymnastics' },
  { id: 'cheerleading', name: 'Cheerleading' },
  { id: 'dance', name: 'Dance' },
  { id: 'weightlifting', name: 'Weightlifting' },
  { id: 'powerlifting', name: 'Powerlifting' },
  { id: 'other', name: 'Other' },
];

const NutritionSetupWizard = ({ onComplete, existingData }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    weight_lbs: existingData?.settings?.weight_lbs || '',
    height_feet: '',
    height_inches: '',
    age: existingData?.settings?.age || '',
    gender: existingData?.settings?.gender || 'male',
    activity_level: existingData?.settings?.activity_level || 'moderate',
    sports: existingData?.settings?.sports || [],
    weight_goal: existingData?.settings?.weight_goal || 'maintain',
    target_weight: '',
    weekly_change_lbs: existingData?.settings?.weekly_change_lbs || 1,
    daily_activity_hours: 1.5,
    climate: 'normal'
  });
  const [calculatedTargets, setCalculatedTargets] = useState(null);

  // Convert total inches to feet/inches on load
  useEffect(() => {
    if (existingData?.settings?.height_inches) {
      const totalInches = existingData.settings.height_inches;
      setFormData(prev => ({
        ...prev,
        height_feet: Math.floor(totalInches / 12).toString(),
        height_inches: (totalInches % 12).toString()
      }));
    }
  }, [existingData]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSport = (sportId) => {
    setFormData(prev => {
      const sports = prev.sports.includes(sportId)
        ? prev.sports.filter(s => s !== sportId)
        : [...prev.sports, sportId];
      return { ...prev, sports };
    });
  };

  const calculateTargets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const height_inches = (parseInt(formData.height_feet) || 0) * 12 + (parseInt(formData.height_inches) || 0);
      
      const response = await axios.post(`${API}/nutrition/calculate-targets`, {
        weight_lbs: parseFloat(formData.weight_lbs),
        height_inches: height_inches,
        age: parseInt(formData.age),
        gender: formData.gender,
        activity_level: formData.activity_level,
        sports: formData.sports,
        weight_goal: formData.weight_goal,
        weekly_change_lbs: parseFloat(formData.weekly_change_lbs),
        daily_activity_hours: parseFloat(formData.daily_activity_hours),
        climate: formData.climate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalculatedTargets(response.data);
      setStep(4);
    } catch (error) {
      toast.error('Failed to calculate targets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveAndComplete = () => {
    toast.success('Nutrition targets saved!');
    onComplete(calculatedTargets);
  };

  const totalSteps = 4;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-aura-text-muted">Step {step} of {totalSteps}</span>
          <span className="text-sm text-aura-text-muted">{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <Progress value={(step / totalSteps) * 100} className="h-2" />
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <Scale className="w-5 h-5 text-aura-primary" />
              Body Measurements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Weight (lbs)</Label>
                <Input
                  type="number"
                  value={formData.weight_lbs}
                  onChange={(e) => updateField('weight_lbs', e.target.value)}
                  placeholder="165"
                  className="h-12 bg-aura-bg border-white/10 text-white text-lg"
                  data-testid="weight-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Age</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => updateField('age', e.target.value)}
                  placeholder="17"
                  className="h-12 bg-aura-bg border-white/10 text-white text-lg"
                  data-testid="age-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Height</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Input
                    type="number"
                    value={formData.height_feet}
                    onChange={(e) => updateField('height_feet', e.target.value)}
                    placeholder="5"
                    className="h-12 bg-aura-bg border-white/10 text-white text-lg pr-12"
                    data-testid="height-feet-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-muted">ft</span>
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    value={formData.height_inches}
                    onChange={(e) => updateField('height_inches', e.target.value)}
                    placeholder="10"
                    className="h-12 bg-aura-bg border-white/10 text-white text-lg pr-12"
                    data-testid="height-inches-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-muted">in</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Gender</Label>
              <div className="grid grid-cols-2 gap-4">
                {['male', 'female'].map(g => (
                  <button
                    key={g}
                    onClick={() => updateField('gender', g)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.gender === g 
                        ? 'border-aura-primary bg-aura-primary/10 text-white' 
                        : 'border-white/10 text-aura-text-muted hover:border-white/30'
                    }`}
                  >
                    <span className="capitalize font-medium">{g}</span>
                  </button>
                ))}
              </div>
            </div>

            <Button 
              onClick={() => setStep(2)}
              disabled={!formData.weight_lbs || !formData.age || !formData.height_feet}
              className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover text-white"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Sports & Activity */}
      {step === 2 && (
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <Activity className="w-5 h-5 text-aura-secondary" />
              Sports & Activity Level
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                Select Your Sports (multiple allowed)
              </Label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                {SPORTS_LIST.map(sport => (
                  <button
                    key={sport.id}
                    onClick={() => toggleSport(sport.id)}
                    className={`p-2 rounded-lg text-sm transition-all ${
                      formData.sports.includes(sport.id)
                        ? 'bg-aura-primary text-white'
                        : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                    }`}
                  >
                    {sport.name}
                  </button>
                ))}
              </div>
              {formData.sports.length > 0 && (
                <p className="text-sm text-aura-text-muted">
                  Selected: {formData.sports.length} sport(s)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Activity Level</Label>
              <Select value={formData.activity_level} onValueChange={(v) => updateField('activity_level', v)}>
                <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  <SelectItem value="sedentary" className="text-white">Sedentary (little exercise)</SelectItem>
                  <SelectItem value="light" className="text-white">Light (1-2 days/week)</SelectItem>
                  <SelectItem value="moderate" className="text-white">Moderate (3-5 days/week)</SelectItem>
                  <SelectItem value="active" className="text-white">Active (6-7 days/week)</SelectItem>
                  <SelectItem value="very_active" className="text-white">Very Active (2x/day)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                Daily Training Hours
              </Label>
              <Select 
                value={formData.daily_activity_hours.toString()} 
                onValueChange={(v) => updateField('daily_activity_hours', parseFloat(v))}
              >
                <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  <SelectItem value="0.5" className="text-white">30 minutes</SelectItem>
                  <SelectItem value="1" className="text-white">1 hour</SelectItem>
                  <SelectItem value="1.5" className="text-white">1.5 hours</SelectItem>
                  <SelectItem value="2" className="text-white">2 hours</SelectItem>
                  <SelectItem value="2.5" className="text-white">2.5 hours</SelectItem>
                  <SelectItem value="3" className="text-white">3+ hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)}
                className="flex-1 h-12 border-white/20 text-white"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)}
                disabled={formData.sports.length === 0}
                className="flex-1 h-12 bg-aura-primary hover:bg-aura-primary-hover text-white"
              >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Weight Goals */}
      {step === 3 && (
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              Weight Goals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Goal</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'lose', label: 'Lose Weight', color: 'red' },
                  { id: 'maintain', label: 'Maintain', color: 'blue' },
                  { id: 'gain', label: 'Gain Weight', color: 'green' }
                ].map(goal => (
                  <button
                    key={goal.id}
                    onClick={() => updateField('weight_goal', goal.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.weight_goal === goal.id
                        ? `border-${goal.color}-500 bg-${goal.color}-500/10 text-white`
                        : 'border-white/10 text-aura-text-muted hover:border-white/30'
                    }`}
                  >
                    {goal.label}
                  </button>
                ))}
              </div>
            </div>

            {formData.weight_goal !== 'maintain' && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Target Weight (lbs)
                  </Label>
                  <Input
                    type="number"
                    value={formData.target_weight}
                    onChange={(e) => updateField('target_weight', e.target.value)}
                    placeholder={formData.weight_goal === 'lose' ? '155' : '175'}
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Weekly Change Rate
                  </Label>
                  <Select 
                    value={formData.weekly_change_lbs.toString()} 
                    onValueChange={(v) => updateField('weekly_change_lbs', parseFloat(v))}
                  >
                    <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="0.5" className="text-white">0.5 lbs/week (Slow & Steady)</SelectItem>
                      <SelectItem value="1" className="text-white">1 lb/week (Recommended)</SelectItem>
                      <SelectItem value="1.5" className="text-white">1.5 lbs/week (Aggressive)</SelectItem>
                      <SelectItem value="2" className="text-white">2 lbs/week (Maximum)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-aura-text-muted">
                    {formData.weight_goal === 'lose' 
                      ? 'Slower rates help preserve muscle mass'
                      : 'Slower rates minimize fat gain while building muscle'}
                  </p>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(2)}
                className="flex-1 h-12 border-white/20 text-white"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button 
                onClick={calculateTargets}
                disabled={loading}
                className="flex-1 h-12 bg-aura-primary hover:bg-aura-primary-hover text-white"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate My Targets
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Results */}
      {step === 4 && calculatedTargets && (
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Your Personalized Targets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* BMR/TDEE Info */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">{calculatedTargets.bmr}</p>
                <p className="text-xs text-aura-text-muted uppercase">BMR</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">{calculatedTargets.tdee}</p>
                <p className="text-xs text-aura-text-muted uppercase">TDEE</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">{calculatedTargets.bmi}</p>
                <p className="text-xs text-aura-text-muted uppercase">BMI</p>
              </div>
            </div>

            {/* Daily Calories */}
            <div className="p-5 bg-gradient-to-r from-red-500/20 to-orange-500/10 rounded-xl border border-red-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-aura-text-muted uppercase tracking-wider">Daily Calories</p>
                  <p className="text-4xl font-bold text-white mt-1">
                    {calculatedTargets.calories.daily_goal}
                  </p>
                </div>
                {calculatedTargets.calories.surplus > 0 && (
                  <Badge className="bg-green-500/20 text-green-400 border-0">
                    +{calculatedTargets.calories.surplus} surplus
                  </Badge>
                )}
                {calculatedTargets.calories.deficit > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border-0">
                    -{calculatedTargets.calories.deficit} deficit
                  </Badge>
                )}
              </div>
            </div>

            {/* Macros */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <p className="text-xs text-blue-400 uppercase">Protein</p>
                <p className="text-2xl font-bold text-white">{calculatedTargets.macros.protein}g</p>
                <p className="text-xs text-aura-text-muted">{calculatedTargets.protein.per_lb}g/lb</p>
              </div>
              <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <p className="text-xs text-yellow-400 uppercase">Carbs</p>
                <p className="text-2xl font-bold text-white">{calculatedTargets.macros.carbs}g</p>
              </div>
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-xs text-purple-400 uppercase">Fat</p>
                <p className="text-2xl font-bold text-white">{calculatedTargets.macros.fat}g</p>
              </div>
            </div>

            {/* Hydration */}
            <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30 flex items-center gap-4">
              <Droplets className="w-8 h-8 text-cyan-400" />
              <div>
                <p className="text-sm text-cyan-400 uppercase">Daily Hydration Goal</p>
                <p className="text-xl font-bold text-white">
                  {calculatedTargets.hydration.daily_oz} oz 
                  <span className="text-sm text-aura-text-muted ml-2">
                    ({calculatedTargets.hydration.daily_cups} cups)
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(3)}
                className="flex-1 h-12 border-white/20 text-white"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Adjust
              </Button>
              <Button 
                onClick={saveAndComplete}
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="w-5 h-5 mr-2" />
                Save & Start Fueling
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NutritionSetupWizard;
